/**
 * VenuesPage ✦ iter229 — Venue Inspiration (interactive portfolio).
 *
 * Hero with live auto-stats → interactive Leaflet map of every
 * completed EventDrop event (clustered, package-coloured pins,
 * filterable, mobile bottom-sheet) → existing venue card grid
 * (curated venues you can click into for detail pages) → final CTA.
 *
 * One pin per event so a venue that's hosted 3 events shows 3
 * stacked markers when zoomed in.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import ServicesNav from '../components/services/ServicesNav';
import BrandedPlaceholder from '../components/services/BrandedPlaceholder';
import EnquiryForm from '../components/services/EnquiryForm';
import VenueMap from '../components/VenueMap';
import EventTimeline from '../components/EventTimeline';
import {
  SERIF, IVORY, IVORY_DIM, IVORY_GHOST, IVORY_FAINT,
  GOLD, GOLD_MUTED, GOLD_FAINT, GOLD_HAIRLINE,
  WALNUT_CARD, PAGE_BG, PILL_BUTTON_PRIMARY, PILL_BUTTON_GHOST,
} from '../components/services/_tokens';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VenueCard = ({ v, boothLabels }) => {
  const cover = v.image_url || v.cover_image_url || null;
  const booths = (v.booths_used || []).slice(0, 4);
  const isPartner = (v.event_count || 0) >= 10;
  const isTrusted = !isPartner && (v.event_count || 0) >= 5;
  return (
    <Link to={`/venues/${v.slug}`} data-testid={`venue-card-${v.slug}`} style={{
      textDecoration: 'none', color: 'inherit',
      background: WALNUT_CARD, border: `1px solid ${isPartner ? GOLD : GOLD_FAINT}`,
      borderRadius: 12, overflow: 'hidden',
      boxShadow: isPartner
        ? '0 22px 60px rgba(156,124,46,0.32)'
        : '0 18px 50px rgba(0,0,0,0.45)',
      display: 'flex', flexDirection: 'column',
      transition: 'transform 220ms ease, box-shadow 220ms ease',
      position: 'relative',
    }}
    onMouseEnter={(e) => { e.currentTarget.style.transform = 'translateY(-4px)'; }}
    onMouseLeave={(e) => { e.currentTarget.style.transform = 'translateY(0)'; }}
    >
      {(isPartner || isTrusted) && (
        <span
          data-testid={`venue-card-badge-${v.slug}`}
          style={{
            position: 'absolute', top: 14, left: 14, zIndex: 2,
            background: isPartner ? GOLD : 'rgba(20,12,8,0.78)',
            color: isPartner ? '#1A120A' : GOLD,
            border: isPartner ? 'none' : `1px solid ${GOLD}`,
            fontFamily: SERIF, fontStyle: 'italic',
            fontSize: 10, letterSpacing: '0.22em', textTransform: 'uppercase',
            padding: '5px 11px', borderRadius: 999,
            boxShadow: '0 6px 16px rgba(0,0,0,0.32)',
          }}
        >{isPartner ? '✦ Partner Venue' : '★ Trusted Venue'}</span>
      )}      {cover ? (
        <div style={{
          backgroundImage: `url(${cover.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${cover}` : cover})`,
          backgroundSize: 'cover', backgroundPosition: 'center',
          aspectRatio: '4 / 3',
        }} />
      ) : (
        <BrandedPlaceholder label={v.name} aspectRatio="4 / 3" />
      )}
      <div style={{ padding: '18px 20px 22px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <h3 style={{
          fontFamily: SERIF, fontWeight: 500, fontSize: 18,
          color: IVORY, margin: '0 0 4px', letterSpacing: '-0.005em',
        }}>{v.name}</h3>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic', fontSize: 12.5,
          color: IVORY_DIM, marginBottom: 12,
        }}>{v.location}</div>
        {booths.length > 0 && (
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
            {booths.map(b => (
              <span key={b} style={{
                fontFamily: SERIF, fontSize: 11, color: GOLD,
                background: 'rgba(212,170,82,0.10)',
                padding: '3px 9px', borderRadius: 999,
                letterSpacing: '0.04em',
              }}>{boothLabels[b] || b}</span>
            ))}
          </div>
        )}
        <div style={{ marginTop: 'auto', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span style={{ fontFamily: SERIF, fontStyle: 'italic', fontSize: 12, color: IVORY_GHOST }}>
            {v.event_count || 0} event{v.event_count === 1 ? '' : 's'}
            {v.last_event_date ? ` · ${v.last_event_date.slice(0, 7)}` : ''}
          </span>
          <span style={{ fontFamily: SERIF, fontSize: 12, color: GOLD, fontStyle: 'italic' }}>
            View events →
          </span>
        </div>
      </div>
    </Link>
  );
};

const VenuesPage = () => {
  const [venues, setVenues] = useState([]);
  const [boothLabels, setBoothLabels] = useState({});
  const [stats, setStats] = useState(null);
  const [enquiryOpen, setEnquiryOpen] = useState(false);
  const [view, setView] = useState('map'); // iter233 — 'map' | 'timeline'

  useEffect(() => {
    document.title = 'Venue Inspiration ✦ EventDrop';
    let cancelled = false;
    (async () => {
      try {
        const [v, s] = await Promise.all([
          axios.get(`${API}/venues`),
          axios.get(`${API}/venues/stats`),
        ]);
        if (cancelled) return;
        setVenues(v.data.venues || []);
        setBoothLabels(v.data.booth_labels || {});
        setStats(s.data || null);
      } catch { /* empty state below */ }
    })();
    return () => { cancelled = true; };
  }, []);

  // Only show venue cards that have at least one photo.
  const photographedVenues = venues.filter(
    (v) => v && (v.image_url || v.cover_image_url),
  );

  return (
    <div data-testid="venues-page" style={{
      minHeight: '100vh', background: PAGE_BG, color: IVORY, fontFamily: SERIF,
    }}>
      <ServicesNav active="services" />

      {/* HERO */}
      <section style={{
        padding: 'clamp(72px, 12vw, 130px) clamp(20px, 5vw, 60px) clamp(30px, 5vw, 50px)',
        textAlign: 'center',
      }}>
        <div style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
          color: GOLD_MUTED, marginBottom: 18,
        }}>✦ Venue Inspiration</div>
        <h1 data-testid="venues-headline" style={{
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(34px, 6vw, 56px)', lineHeight: 1.05,
          color: IVORY, margin: 0, letterSpacing: '-0.022em',
        }}>Every venue. Every event. Every memory.</h1>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          fontSize: 'clamp(14px, 1.5vw, 17px)', color: IVORY_DIM,
          maxWidth: 660, margin: '22px auto 0', lineHeight: 1.65,
        }}>
          Explore real weddings, parties and corporate events where EventDrop has created
          unforgettable memories.
        </p>
      </section>

      {/* LIVE STATS */}
      {stats && (
        <section data-testid="venue-stats-row" style={{
          padding: '0 clamp(20px, 5vw, 60px) clamp(40px, 6vw, 70px)',
        }}>
          <div style={{
            maxWidth: 1180, margin: '0 auto',
            display: 'grid', gap: 14,
            gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
          }}>
            <StatTile label="Events Completed" value={stats.events_completed} testid="stat-events-completed" />
            <StatTile label="Venues Visited" value={stats.venues_visited} testid="stat-venues-visited" />
            <StatTile label="Average Rating" value={stats.average_rating ? `${stats.average_rating} ★` : '—'} testid="stat-avg-rating" />
            <StatTile label="Photos Captured" value={stats.photos_captured?.toLocaleString() || 0} testid="stat-photos" />
            <StatTile label="Counties Covered" value={stats.counties_covered} testid="stat-counties" />
          </div>
        </section>
      )}

      {/* INTERACTIVE MAP / TIMELINE */}
      <section data-testid="venue-map-section" style={{
        padding: 'clamp(20px, 4vw, 40px) clamp(20px, 5vw, 60px) clamp(60px, 8vw, 100px)',
        background: 'linear-gradient(180deg, rgba(212,170,82,0.04), transparent)',
        borderTop: `1px solid ${GOLD_HAIRLINE}`,
      }}>
        <div style={{ maxWidth: 1280, margin: '0 auto' }}>
          <SectionEyebrow>✦ {view === 'map' ? 'Browse the map' : 'Walk the timeline'}</SectionEyebrow>
          <SectionHeading>
            {view === 'map' ? "Explore where we've been" : 'Every memory, in order'}
          </SectionHeading>
          <p style={{
            fontFamily: SERIF, fontStyle: 'italic', textAlign: 'center',
            fontSize: 14, color: IVORY_DIM, maxWidth: 600,
            margin: '12px auto 22px', lineHeight: 1.6,
          }}>
            {view === 'map'
              ? 'Every pin is a real EventDrop event. Click to open the full story — gallery, package, venue and customer review.'
              : 'Scroll through our story chronologically — every event we\'ve been part of, newest first.'}
          </p>
          {/* iter233 — Map ↔ Timeline view toggle */}
          <div data-testid="venues-view-toggle" style={{
            display: 'flex', justifyContent: 'center', gap: 6,
            marginBottom: 26, padding: 4,
            background: 'rgba(255,252,246,0.06)',
            border: `1px solid ${GOLD_HAIRLINE}`,
            borderRadius: 999, width: 'fit-content', margin: '0 auto 26px',
          }}>
            {[
              { key: 'map', label: '✦ Map view' },
              { key: 'timeline', label: '✦ Timeline view' },
            ].map(opt => (
              <button
                key={opt.key}
                type="button"
                onClick={() => setView(opt.key)}
                data-testid={`venues-view-${opt.key}`}
                aria-pressed={view === opt.key}
                style={{
                  background: view === opt.key ? GOLD : 'transparent',
                  color: view === opt.key ? '#FFF8E7' : IVORY,
                  border: 'none', borderRadius: 999,
                  padding: '9px 22px', cursor: 'pointer',
                  fontFamily: SERIF, fontSize: 13,
                  letterSpacing: '0.14em', textTransform: 'uppercase',
                  transition: 'background 0.18s ease, color 0.18s ease',
                }}>{opt.label}</button>
            ))}
          </div>
          {view === 'map'
            ? <VenueMap variant="full" />
            : <EventTimeline />}
        </div>
      </section>

      {/* VENUE GRID */}
      {photographedVenues.length > 0 && (
        <section style={{
          padding: 'clamp(40px, 6vw, 70px) clamp(20px, 5vw, 60px) clamp(80px, 10vw, 120px)',
          borderTop: `1px solid ${GOLD_HAIRLINE}`,
        }}>
          <div style={{ maxWidth: 1180, margin: '0 auto' }}>
            <SectionEyebrow>✦ Browse by venue</SectionEyebrow>
            <SectionHeading>Featured venues we love</SectionHeading>
            <div style={{
              marginTop: 32,
              display: 'grid', gap: 22,
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
            }}>
              {photographedVenues.map(v => (
                <VenueCard key={v.slug} v={v} boothLabels={boothLabels} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section data-testid="venues-final-cta" style={{
        padding: 'clamp(56px, 8vw, 90px) clamp(20px, 5vw, 60px)',
        textAlign: 'center', borderTop: `1px solid ${GOLD_HAIRLINE}`,
      }}>
        <h2 style={{
          fontFamily: SERIF, fontWeight: 500,
          fontSize: 'clamp(22px, 3.6vw, 32px)', margin: 0, color: IVORY,
          letterSpacing: '-0.01em',
        }}>Have a venue in mind?</h2>
        <p style={{
          fontFamily: SERIF, fontStyle: 'italic',
          color: IVORY_DIM, fontSize: 'clamp(14px, 1.4vw, 16px)',
          margin: '14px auto 26px', maxWidth: 480, lineHeight: 1.55,
        }}>
          Tell us where the night is happening — we&apos;ll bring the right booth for the room.
        </p>
        <button
          type="button"
          onClick={() => setEnquiryOpen(true)}
          data-testid="venues-cta-enquire"
          style={PILL_BUTTON_PRIMARY}
        >Check Availability ✦</button>
      </section>

      {enquiryOpen && (
        <EnquiryForm onClose={() => setEnquiryOpen(false)} sourcePage="venues" />
      )}
    </div>
  );
};

const StatTile = ({ label, value, testid }) => (
  <div data-testid={testid} style={{
    background: WALNUT_CARD, border: `1px solid ${GOLD_FAINT}`,
    borderRadius: 12, padding: '22px 18px', textAlign: 'center',
  }}>
    <div style={{
      fontFamily: SERIF, fontWeight: 500,
      fontSize: 'clamp(22px, 3vw, 32px)', color: GOLD,
      letterSpacing: '-0.01em', lineHeight: 1.1, marginBottom: 6,
    }}>{value ?? '—'}</div>
    <div style={{
      fontFamily: SERIF, fontStyle: 'italic',
      fontSize: 11.5, letterSpacing: '0.16em', textTransform: 'uppercase',
      color: IVORY_DIM,
    }}>{label}</div>
  </div>
);

const SectionEyebrow = ({ children }) => (
  <div style={{
    fontFamily: SERIF, fontStyle: 'italic',
    fontSize: 11, letterSpacing: '0.32em', textTransform: 'uppercase',
    color: GOLD_MUTED, textAlign: 'center', marginBottom: 12,
  }}>{children}</div>
);

const SectionHeading = ({ children }) => (
  <h2 style={{
    fontFamily: SERIF, fontWeight: 500, textAlign: 'center',
    fontSize: 'clamp(26px, 4vw, 38px)', letterSpacing: '-0.02em',
    color: IVORY, margin: 0, lineHeight: 1.1,
  }}>{children}</h2>
);

export default VenuesPage;
